import React from 'react';
import { ClbModal, ClbButton, ClbIcon, type ButtonsFooterType } from '@celebration/react';
import type { FeedbackType } from '@/contexts/FeedbackContext';
import styles from './FeedbackModal.module.scss';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: FeedbackType;
  title: string;
  message: string;
}

/**
 * Centralized feedback modal for displaying errors, warnings, and info messages
 * Used by FeedbackContext to show consistent feedback across the application
 */
const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message,
}) => {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return 'cancel-filled';
      case 'warning':
        return 'warning-fill';
      case 'info':
        return 'info-fill';
      case 'success':
        return 'checkbox-circle-fill';
      default:
        return 'cancel-filled';
    }
  };

  const getIconClassName = () => {
    switch (type) {
      case 'error':
        return styles.iconError;
      case 'warning':
        return styles.iconWarning;
      case 'info':
        return styles.iconInfo;
      case 'success':
        return styles.iconSuccess;
      default:
        return styles.iconError;
    }
  };

  const customFooter = (
    <div className={styles.footer}>
      <ClbButton
        buttonStyle="primary"
        size="md"
        label="Ok"
        onClick={onClose}
      />
    </div>
  );

  return (
    <ClbModal
      open={isOpen}
      onClose={onClose}
      title=""
      itemsFooter={[] as unknown as ButtonsFooterType}
      customFooter={customFooter}
      style={{ maxWidth: '500px' }}
    >
      <div className={styles.content}>
        <div className={styles.iconWrapper}>
          <ClbIcon
            icon={getIcon()}
            size="3xlg"
            className={getIconClassName()}
          />
        </div>
        <div className={styles.textContent}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.message}>{message}</p>
        </div>
      </div>
    </ClbModal>
  );
};

export default FeedbackModal;
